import type { PublicConfig } from "../lib/api";
import type { Order, OrderShippingParcel, User } from "../types";

/** Labels and tracking belong to distinct immutable parcels, never one merged URL. */
export function normalizeOrderParcels(value: unknown): OrderShippingParcel[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length < 1 || value.length > 10) throw new Error("Il piano dei colli richiede una verifica.");
  const text = (input: unknown, max: number): input is string => typeof input === "string" && input.length > 0 && input.length <= max && !/[\u0000-\u001f\u007f]/.test(input);
  const link = (input: unknown): string | null => {
    if (input === undefined || input === null) return null;
    if (!text(input, 1000) || httpsLink(input) === null) throw new Error("Il collegamento della spedizione non è verificabile.");
    return input;
  };
  return value.map((raw: unknown, index) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("Il piano dei colli richiede una verifica.");
    const p = raw as Record<string, unknown>;
    if (p.parcelIndex !== index + 1 || !Number.isSafeInteger(p.weightGrams) || (p.weightGrams as number) < 50 || (p.weightGrams as number) > 25000
      || !["prepared", "requesting", "purchased", "unknown", "failed", "cancelled"].includes(String(p.state))
      || !["pending", "shipped", "delivered", "exception", "returned"].includes(String(p.deliveryState))
      || (p.trackingNumber !== null && p.trackingNumber !== undefined && !text(p.trackingNumber, 100))) throw new Error("Il piano dei colli richiede una verifica.");
    return { parcelIndex: index + 1, weightGrams: p.weightGrams as number, state: String(p.state), deliveryState: String(p.deliveryState),
      trackingNumber: typeof p.trackingNumber === "string" ? p.trackingNumber : null, trackingUrl: link(p.trackingUrl), labelUrl: link(p.labelUrl) };
  });
}

export function normalizeOrder(raw: Record<string, unknown>): Order {
  return {
    id: String(raw.id), listingId: String(raw.listingId || ""),
    title: String(raw.listingTitle || `Ordine ${String(raw.id).slice(0, 8)}`),
    status: String(raw.status), buyerId: String(raw.buyerId), sellerId: String(raw.sellerId),
    totalEur: Number(raw.totalCents) / 100, createdAt: String(raw.createdAt),
    disputeDeadline: typeof raw.disputeDeadline === "string" ? raw.disputeDeadline : null,
    trackingUrl: typeof raw.trackingUrl === "string" ? raw.trackingUrl : null,
    labelUrl: typeof raw.labelUrl === "string" ? raw.labelUrl : null,
    sellerReceivesEur: raw.sellerReceivesCents === undefined ? null : Number(raw.sellerReceivesCents) / 100,
    checkoutExpiresAt: typeof raw.checkoutExpiresAt === "string" ? raw.checkoutExpiresAt : null,
    fulfillmentMode: raw.fulfillmentMode === 'pickup' ? 'pickup' : 'shipping',
    shippingParcels: normalizeOrderParcels(raw.shippingParcels),
  };
}

export function orderActions(order: Order, user: User, config: PublicConfig | null, now = Date.now()) {
  const capabilities = (config?.capabilities || {}) as Record<string, unknown>;
  const authenticated = Boolean(config?.configured && user.emailVerified);
  const deadline = order.disputeDeadline ? Date.parse(order.disputeDeadline) : null;
  const validDeadline = deadline !== null && Number.isFinite(deadline);
  const checkoutDeadline = order.checkoutExpiresAt ? Date.parse(order.checkoutExpiresAt) : null;
  const isBuyer = user.id === order.buyerId;
  const isSeller = user.id === order.sellerId;
  return {
    isBuyer, isSeller,
    checkout: authenticated && isBuyer && order.status === "awaiting_payment" && config?.checkoutEnabled === true
      && checkoutDeadline !== null && Number.isFinite(checkoutDeadline) && checkoutDeadline > now,
    label: authenticated && isSeller && order.fulfillmentMode !== 'pickup' && order.status === "paid" && Boolean(capabilities.payments && capabilities.shipping),
    pickup: authenticated && isBuyer && order.fulfillmentMode === 'pickup' && order.status === 'paid',
    dispute: authenticated && isBuyer && ["paid", "shipped", "delivered"].includes(order.status)
      && (deadline === null || (validDeadline && deadline > now)),
    complete: authenticated && isBuyer && order.status === "delivered" && validDeadline && deadline <= now
      && Boolean(config?.checkoutEnabled),
    review: authenticated && (isBuyer || isSeller) && order.status === "completed",
  };
}

export function orderStatus(status: string) {
  return ({ awaiting_payment: "In attesa del pagamento", payment_review: "Pagamento in verifica", paid: "Pagamento confermato", shipped: "Spedito", delivered: "Consegnato · periodo di protezione", completed: "Transazione conclusa", disputed: "Contestazione aperta", refund_pending: "Rimborso in verifica · non ancora completato", refunded: "Rimborsato", cancelled: "Annullato" } as Record<string, string>)[status] || status;
}

export function httpsLink(value: string | null): string | null {
  if (!value) return null;
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password && !/[\u0000-\u0020\u007f]/.test(value) ? url.toString() : null; } catch { return null; }
}
